import { type ReactNode } from "react";
import { useAuth, useAuthExpiryWatcher } from "../hooks/useAuth";

/**
 * Sign-in link that comes back to the page the operator was actually on.
 * Without `redirect_after`, every expired session on `/team` ends on
 * the dashboard — the same "why am I on the start page again" as the gate bug,
 * arrived at by a different route. The server re-checks this value and drops
 * anything that isn't a site-absolute path.
 */
function signInHref(): string {
  const here = window.location.pathname + window.location.search;
  return `/api/auth/microsoft/start?redirect_after=${encodeURIComponent(here)}`;
}

/**
 * Wraps the app routes. Renders children only once the session is resolved.
 * Two paths reach a cookie: browser-native Microsoft OAuth (the button) or
 * `jarvis webui open` from the local jarvis-skill (Bearer → skill-bridge).
 *
 * The "unavailable" branch matters as much as the sign-in one: right after a
 * deploy the API container is still starting and the proxy answers 502. That
 * is not a missing session, and offering a sign-in button for it sends the
 * operator through an OAuth round trip that cannot help.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { status, refresh } = useAuth();
  useAuthExpiryWatcher();

  if (status === "loading") {
    return (
      <div className="auth-guard">
        <p className="auth-guard-text">Loading...</p>
      </div>
    );
  }

  if (status === "unauthenticated") {
    return (
      <div className="auth-guard">
        <div className="auth-guard-card">
          <span className="auth-guard-mark">d</span>
          <h1>Sign in to Jarvis</h1>
          <p>You're not signed in.</p>
          <a className="auth-guard-cta" href={signInHref()}>
            Sign in with Microsoft
          </a>
          <p className="auth-guard-hint">
            Or, from your terminal: <code>jarvis webui open</code>
          </p>
        </div>
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div className="auth-guard">
        <div className="auth-guard-card">
          <span className="auth-guard-mark">d</span>
          <h1>Backend unreachable</h1>
          <p>
            The API didn't answer. If a deploy just landed, it's still coming
            up — your session is intact.
          </p>
          <button className="auth-guard-cta" type="button" onClick={refresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
